import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import DashboardIcon from "@material-ui/icons/Dashboard";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import BusinessIcon from "@material-ui/icons/Business";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import * as ROUTES from "../../../constants/routes";
import { withFirebase } from "../../Firebase";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: "100vh",
  },
  appBar: {
    zIndex: 1200,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,.1)",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    zIndex: 1100,
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    background: "#1e1e2d",
    borderRight: "none",
    zIndex: 1100,
    overflowX: "hidden",
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1,
    },
    background: "#1e1e2d",
    borderRight: "none",
    zIndex: 1100,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    minHeight: "64px !important",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: "#f5f8fa",
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  contentToolbar: {
    ...theme.mixins.toolbar,
    minHeight: "64px !important",
  },
  listItem: {
    color: "#92929f",
    margin: theme.spacing(1, 1),
    borderRadius: "8px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(102, 126, 234, 0.1)",
      color: "#667eea",
      "& $listItemIcon": {
        color: "#667eea",
      },
    },
  },
  activeListItem: {
    backgroundColor: "rgba(102, 126, 234, 0.15) !important",
    color: "#667eea !important",
    "& $listItemIcon": {
      color: "#667eea !important",
    },
  },
  listItemIcon: {
    color: "#92929f",
    minWidth: "40px",
    transition: "color 0.3s ease",
  },
  listItemText: {
    "& .MuiTypography-body1": {
      fontWeight: 500,
      fontSize: "0.95rem",
    },
  },
  divider: {
    backgroundColor: "rgba(255,255,255,0.1)",
    margin: theme.spacing(2, 1),
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  logoText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.25rem",
    letterSpacing: "0.5px",
  },
  logoIcon: {
    color: "#667eea",
    fontSize: "2rem",
  },
  title: {
    flexGrow: 1,
    fontWeight: 600,
  },
  mobileDrawer: {
    background: "#1e1e2d",
    borderRight: "none",
  },
}));

const AdminLayoutBase = ({ children, firebase, authUser }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const [open, setOpen] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const menuItems = [
    {
      text: "Checkout",
      icon: <ShoppingCartIcon />,
      path: ROUTES.HOME,
    },
    {
      text: "Produtos",
      icon: <DashboardIcon />,
      path: ROUTES.ADMIN_PRODUCTS,
    },
    {
      text: "Áreas de Entrega",
      icon: <LocalShippingIcon />,
      path: ROUTES.ADMIN_DELIVERY_AREAS,
    },
    {
      text: "Organizações",
      icon: <BusinessIcon />,
      path: ROUTES.ADMIN_ORGANIZATIONS,
    },
  ];

  const isActive = (path) => {
    if (path === ROUTES.HOME) {
      return location.pathname === path || location.pathname === "/admin/checkout";
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <>
      <div className={classes.toolbar}>
        <div className={classes.logoContainer}>
          <LocalShippingIcon className={classes.logoIcon} />
          {(open || mobileOpen) && (
            <Typography variant="h6" className={classes.logoText}>
              Delivery Admin
            </Typography>
          )}
        </div>
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} style={{ color: "#92929f" }}>
            {open ? <ChevronLeftIcon /> : <></>}
          </IconButton>
        )}
      </div>
      <Divider className={classes.divider} />
      <List>
        {menuItems.map((item) => (
          <Tooltip 
            key={item.text}
            title={item.text} 
            placement="right" 
            disableHoverListener={open || mobileOpen}
          >
            <ListItem
              button
              component={Link}
              to={item.path}
              className={`${classes.listItem} ${isActive(item.path) ? classes.activeListItem : ""}`}
              onClick={() => isMobile && setMobileOpen(false)}
            >
              <ListItemIcon className={classes.listItemIcon}>
                {item.icon}
              </ListItemIcon>
              {(open || mobileOpen) && (
                <ListItemText 
                  primary={item.text} 
                  className={classes.listItemText}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Divider className={classes.divider} />
      <List>
        <Tooltip 
          title="Conta" 
          placement="right" 
          disableHoverListener={open || mobileOpen}
        >
          <ListItem
            button
            component={Link}
            to={ROUTES.ACCOUNT}
            className={`${classes.listItem} ${isActive(ROUTES.ACCOUNT) ? classes.activeListItem : ""}`}
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <AccountCircleIcon />
            </ListItemIcon>
            {(open || mobileOpen) && (
              <ListItemText 
                primary="Conta" 
                className={classes.listItemText}
              />
            )}
          </ListItem>
        </Tooltip>
      </List>
    </>
  );

  return (
    <div className={classes.root}>
      <AppBar
        position="fixed"
        className={`${classes.appBar} ${!isMobile && open ? classes.appBarShift : ""}`}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap className={classes.title}>
            Painel Administrativo
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          className={`${classes.drawer} ${open ? classes.drawerOpen : classes.drawerClose}`}
          classes={{
            paper: open ? classes.drawerOpen : classes.drawerClose,
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.mobileDrawer,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <main className={classes.content}>
        <div className={classes.contentToolbar} />
        <Box>{children}</Box>
      </main>
    </div>
  );
};

export const AdminLayout = withFirebase(AdminLayoutBase);
